import { BookPicker } from "../components/BookPicker";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarDays, Loader2, RefreshCw, Radio, Zap, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MatchupCard } from "../components/MatchupCard";
import { MatchupDetail } from "../components/MatchupDetail";
import { AIToolsPanel } from "../components/AIToolsPanel";
import { ConsensusParlayBuilder } from "../components/ConsensusParlayBuilder";
import { PlayerProfileModal } from "../components/PlayerProfileModal";
import { SportTabs } from "../components/SportTabs";
import { labelFor, ALL_SPORT_CODES } from "../lib/sports";
import { useSport } from "../context/SportContext";


export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const source = "live";
  const { sport, setSport } = useSport();
  const [matchups, setMatchups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bookVersion, setBookVersion] = useState(0);
  const [playerId, setPlayerId] = useState(null);
  const [status, setStatus] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(null);
  const [showLater, setShowLater] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const reqId = useRef(0);
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });

  const sportStatus = status.find((s) => s.sport === sport) || {};
  const label = labelFor(sport);

  // Group the slate by day; split into "this week" vs "later" (NFL week runs through Monday).
  const { current, later, weekRange } = useMemo(() => {
    const now = new Date();
    const end = new Date(now);
    const daysToSun = (7 - end.getDay()) % 7;
    end.setDate(end.getDate() + daysToSun + (sport === "NFL" ? 1 : 0));
    end.setHours(23, 59, 59, 999);

    const byDay = {};
    for (const m of matchups) {
      const d = new Date(m.commence_time || m.date);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!byDay[key]) byDay[key] = { date: d, games: [] };
      byDay[key].games.push(m);
    }
    const days = Object.values(byDay).sort((a, b) => a.date - b.date);
    const cur = days.filter((g) => g.date <= end);
    const lat = days.filter((g) => g.date > end);
    const fmt = (dt) => dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const start = cur.length ? cur[0].date : now;
    return { current: cur, later: lat, weekRange: `${fmt(start)} – ${fmt(end)}` };
  }, [matchups, sport]);

  const fmtDayHeader = (dt) => dt.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  const laterCount = later.reduce((n, g) => n + g.games.length, 0);

  const loadStatus = useCallback(() => {
    api.get("/slate/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const loadMatchups = useCallback(() => {
    const myId = ++reqId.current;
    setLoading(true);
    setSelected(null);
    api
      .get("/matchups", { params: { sport, source } })
      .then((r) => {
        if (myId === reqId.current) setMatchups(r.data);
      })
      .finally(() => {
        if (myId === reqId.current) setLoading(false);
      });
  }, [sport, source]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  useEffect(() => {
    loadMatchups();
  }, [loadMatchups]);

  // Deep-link from the live-score bar: /?sport=NBA&source=live&open=<matchup_id>
  useEffect(() => {
    const qpSport = searchParams.get("sport");
    const qpSource = searchParams.get("source");
    const qpOpen = searchParams.get("open");
    if (!qpSport && !qpSource && !qpOpen) return;
    if (qpSport && ALL_SPORT_CODES.includes(qpSport)) setSport(qpSport);
    if (qpOpen) setPendingOpen(qpOpen);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (pendingOpen && !loading) {
      setSelected({ id: pendingOpen });
      setPendingOpen(null);
    }
  }, [pendingOpen, loading]);

  const syncLive = async () => {
    if (!user) {
      toast.error("Sign in to pull live data");
      nav("/login");
      return;
    }
    setSyncing(true);
    try {
      await api.post("/slate/sync", { sport });
      loadStatus();
      loadMatchups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Live sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const fmtSynced = (iso) => {
    if (!iso) return "never";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="max-w-[1600px] mx-auto pl-9 pr-3 sm:pl-10 sm:pr-6 lg:px-6 py-6">
      {/* Live status + sync */}
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="flex items-center gap-1.5 rounded-lg bg-[#EF4444]/15 text-red-400 px-2.5 py-1 font-data text-xs font-bold uppercase tracking-wide">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> Live
          </span>
          <span className="font-data text-[11px] text-slate-500">
            synced {fmtSynced(sportStatus.live_synced_at)}
            {sportStatus.credits_remaining != null && ` · ${sportStatus.credits_remaining} credits left`}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <BookPicker canEdit={!!user} onChange={() => (selected ? setBookVersion((v) => v + 1) : loadMatchups())} />
        <button
          data-testid="sync-live-button"
          onClick={syncLive}
          disabled={syncing}
          className="flex items-center gap-2 rounded-lg bg-[#141C2B] border border-[#00E5FF]/40 text-[#00E5FF] text-sm font-semibold px-3 py-2 hover:bg-[#1B273E] disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : `Sync ${label}`}
        </button>
        </div>
      </div>

      {/* Sport tabs */}
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <SportTabs
          value={sport}
          onChange={setSport}
          counts={Object.fromEntries(status.map((s) => [s.sport, s.live_games]))}
        />
        <span className="flex items-center gap-1.5 text-sm text-slate-400 font-data">
          <CalendarDays className="h-4 w-4" /> {today}
        </span>
      </div>

      <div className="flex gap-6 items-start">
      {!selected && (
        <AIToolsPanel sport={sport} source={source} hasGames={matchups.length > 0} onOpenBuilder={() => setShowBuilder(true)} />
      )}
      <div className="flex-1 min-w-0">
      {selected ? (
        <MatchupDetail key={`${selected.id}-${bookVersion}-${user?.id || "anon"}`} matchupId={selected.id} onBack={() => setSelected(null)} onOpenPlayer={setPlayerId} onLoaded={loadStatus} />
      ) : (
        <div className="space-y-6">
          <div>
            <h2 className="font-heading text-lg font-bold text-white mb-3">
              {label} Live Slate
            </h2>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
              </div>
            ) : matchups.length === 0 ? (
              <div
                data-testid="empty-slate"
                className="rounded-2xl border border-dashed border-[#233248] bg-[#141C2B]/50 p-10 text-center"
              >
                <Zap className="h-8 w-8 text-slate-600 mx-auto mb-3" />
                <h3 className="font-heading text-white font-bold">No live {label} games loaded</h3>
                <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
                  There may be no {label} games on the board right now, or you haven't synced yet. Pull the latest
                  real sportsbook slate to see today's games.
                </p>
                <button
                  data-testid="empty-sync-button"
                  onClick={syncLive}
                  disabled={syncing}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#00E5FF] text-[#0B0F17] font-semibold px-4 py-2 text-sm hover:bg-[#33EaFF] disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Sync live {label} data
                </button>
              </div>
            ) : (
              <>
                {sport === "NFL" && current.length > 0 && (
                  <div data-testid="nfl-week-header" className="flex items-center gap-2 mb-4">
                    <span className="rounded-lg bg-[#00E5FF]/15 text-[#00E5FF] font-heading font-bold text-sm px-3 py-1.5">
                      This Week
                    </span>
                    <span className="font-data text-xs text-slate-400">{weekRange}</span>
                  </div>
                )}

                {current.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6">No {label} games scheduled this week.</p>
                ) : (
                  current.map((group) => (
                    <div key={group.date.toISOString()} className="mb-6" data-testid="day-group">
                      <h3 className="flex items-center gap-2 font-heading font-bold text-white text-sm mb-3">
                        <CalendarDays className="h-4 w-4 text-[#00E5FF]" />
                        {fmtDayHeader(group.date)}
                        <span className="font-data text-[11px] text-slate-500">{group.games.length} games</span>
                      </h3>
                      <motion.div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,400px),1fr))]">
                        {group.games.map((m, i) => (
                          <MatchupCard key={m.id} matchup={m} index={i} onOpen={setSelected} />
                        ))}
                      </motion.div>
                    </div>
                  ))
                )}

                {later.length > 0 && (
                  <button
                    data-testid="toggle-later-games"
                    onClick={() => setShowLater((v) => !v)}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#233248] bg-[#141C2B] text-slate-200 px-4 py-2 text-sm font-semibold hover:border-[#00E5FF]/50"
                  >
                    {showLater ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {showLater ? "Hide later games" : `Show later games (${laterCount})`}
                  </button>
                )}

                {showLater &&
                  later.map((group) => (
                    <div key={group.date.toISOString()} className="mb-6 mt-4" data-testid="day-group-later">
                      <h3 className="flex items-center gap-2 font-heading font-bold text-slate-300 text-sm mb-3">
                        <CalendarDays className="h-4 w-4 text-slate-500" />
                        {fmtDayHeader(group.date)}
                        <span className="font-data text-[11px] text-slate-500">{group.games.length} games</span>
                      </h3>
                      <motion.div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(min(100%,400px),1fr))]">
                        {group.games.map((m, i) => (
                          <MatchupCard key={m.id} matchup={m} index={i} onOpen={setSelected} />
                        ))}
                      </motion.div>
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      )}
      </div>
      </div>

      <PlayerProfileModal playerId={playerId} open={!!playerId} onClose={() => setPlayerId(null)} />
      <ConsensusParlayBuilder open={showBuilder} onClose={() => setShowBuilder(false)} />
    </div>
  );
}
